/**
 * Dispara o download de um Blob no navegador.
 *
 * POR QUE ISTO EXISTE (bug real, produção 2026-07-14):
 * o padrão anterior era, em ~20 lugares:
 *
 *     link.click()
 *     link.remove()
 *     URL.revokeObjectURL(link.href)   // <-- SÍNCRONO, logo após o clique
 *
 * O `click()` só ENFILEIRA o download; o Chrome lê o blob depois, de forma
 * assíncrona. Revogar a URL na linha seguinte mata o blob antes de o navegador
 * conseguir lê-lo, e o download fica parado para sempre em
 * "Unknown — preparing to download" (a entrada aparece, o arquivo nunca baixa).
 * Quanto maior o arquivo, mais fácil de reproduzir.
 *
 * Aqui a revogação acontece depois, num timeout — tempo de sobra para o
 * navegador ter começado a leitura, e sem vazar a URL (que seguraria o blob
 * inteiro em memória até dar reload na página).
 */
export function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    link.remove()

    // NÃO revogar de imediato: ver comentário acima.
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

/** Mesmo que `downloadBlob`, para respostas que vêm como ArrayBuffer/Blob cru. */
export function downloadPdf(data: BlobPart, filename: string): void {
    downloadBlob(new Blob([data], { type: 'application/pdf' }), filename)
}

/**
 * Como um PDF é entregue ao operador. `print` abre a janela de impressão;
 * `download` salva o arquivo.
 */
export type PdfDelivery = 'print' | 'download'

/**
 * Abre a janela de impressão do navegador com o PDF, SEM baixar o arquivo.
 *
 * Como funciona: o PDF vai para um iframe fora da tela e chamamos `print()` no
 * documento dele. É o único jeito de abrir o diálogo de impressão já apontado
 * para o PDF — `window.print()` na página imprimiria a tela, não o documento.
 *
 * Três detalhes que quebram se mexidos:
 *
 * 1. O iframe NÃO pode ser `display: none`. Um iframe sem caixa de renderização
 *    não carrega o visualizador de PDF em Chrome/Safari, e `print()` não acha
 *    nada para imprimir. Por isso ele fica com 1px e jogado para fora da tela.
 * 2. O iframe NÃO pode ser removido logo após o `print()`. O diálogo é modal e
 *    assíncrono: tirar o iframe do DOM enquanto ele está aberto cancela a
 *    impressão. A limpeza acontece no `afterprint` (ou num timeout de segurança,
 *    para navegadores que não disparam o evento).
 * 3. A blob URL não pode ser revogada de imediato — mesmo motivo do
 *    `downloadBlob` acima.
 *
 * Se o navegador bloquear a impressão pelo iframe (acontece em configurações do
 * Firefox), cai no plano B: abre o PDF numa aba nova, onde o operador imprime
 * pelo visualizador. Melhor que falhar em silêncio.
 */
export function printPdfBlob(blob: Blob): void {
    const url = URL.createObjectURL(blob)

    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.left = '-10000px'
    iframe.style.top = '0'
    iframe.style.width = '1px'
    iframe.style.height = '1px'
    iframe.style.border = '0'
    iframe.src = url

    let cleanedUp = false
    const cleanUp = () => {
        if (cleanedUp) return
        cleanedUp = true
        iframe.remove()
        URL.revokeObjectURL(url)
    }

    const openInNewTab = () => {
        // Não revoga aqui: a aba nova ainda precisa ler o blob.
        cleanedUp = true
        iframe.remove()
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 60_000)
    }

    iframe.onload = () => {
        try {
            const win = iframe.contentWindow
            if (!win) {
                openInNewTab()
                return
            }
            win.addEventListener('afterprint', cleanUp)
            win.focus()
            win.print()
            // Rede de segurança: nem todo navegador dispara `afterprint`.
            setTimeout(cleanUp, 120_000)
        } catch {
            openInNewTab()
        }
    }

    iframe.onerror = openInNewTab

    document.body.appendChild(iframe)
}

/** Entrega o PDF conforme o modo escolhido pela ação do operador. */
export function deliverPdf(
    blob: Blob,
    filename: string,
    delivery: PdfDelivery
): void {
    if (delivery === 'print') printPdfBlob(blob)
    else downloadBlob(blob, filename)
}
